import {
  RESOURCES_BY_ID,
  TECHS_BY_ID,
  BUILDINGS_BY_ID,
  availableWorkers,
  formatNumber,
  visibleJobs,
} from '../../engine';
import type { ResourceBundle } from '../../engine';
import { gameStore } from '../../game/store';
import type { Snapshot } from '../../game/store';
import { Glyph } from '../Glyph';

interface JobsPanelProps {
  snapshot: Snapshot;
}

function bundleText(bundle: ResourceBundle, workers: number): string {
  return Object.entries(bundle)
    .map(
      ([resourceId, perWorker]) =>
        `${formatNumber((perWorker ?? 0) * Math.max(1, workers))} ${
          RESOURCES_BY_ID[resourceId]?.name ?? resourceId
        }/s`,
    )
    .join(', ');
}

export function JobsPanel({ snapshot }: JobsPanelProps) {
  const { state } = snapshot;
  const jobs = visibleJobs(state, snapshot.production);
  const idle = availableWorkers(state);

  return (
    <section className="panel">
      <h2 className="panel-title">
        The Tribe
        <span className="hint">Shift-click for ten, alt-click for all</span>
      </h2>

      <div className="idle-banner">
        <span>
          <strong>{idle}</strong> {idle === 1 ? 'member stands idle' : 'members stand idle'}
        </span>
        <button
          type="button"
          className="btn ghost"
          onClick={() => gameStore.clearJobs()}
          disabled={idle >= Math.floor(state.population)}
        >
          Recall all
        </button>
      </div>

      {jobs.map((job) => {
        const requiredTech = job.def.requiresTech
          ? TECHS_BY_ID[job.def.requiresTech]?.name ?? job.def.requiresTech
          : null;
        const requiredBuilding = job.def.requiresBuilding
          ? BUILDINGS_BY_ID[job.def.requiresBuilding]?.name ?? job.def.requiresBuilding
          : null;

        const step = (event: React.MouseEvent, direction: 1 | -1): void => {
          if (event.altKey) {
            gameStore.assign(job.def.id, direction > 0 ? idle : -job.assigned);
            return;
          }
          const size = event.shiftKey ? 10 : 1;
          gameStore.assign(job.def.id, direction * size);
        };

        return (
          <div
            key={job.def.id}
            className={`job-row${job.unlocked ? '' : ' locked'}`}
            title={job.def.description}
          >
            <Glyph id={job.def.glyph} size={24} tone={job.assigned > 0 ? 'ochre' : 'bone'} />

            <div>
              <div className="job-name">{job.def.workers}</div>
              {job.unlocked ? (
                <div className="job-rates">
                  <span className="out">{bundleText(job.outputs, job.assigned)}</span>
                  {Object.keys(job.inputs).length > 0 ? (
                    <>
                      {' '}
                      · needs <span className="in">{bundleText(job.inputs, job.assigned)}</span>
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="job-rates">
                  Needs {requiredTech ?? requiredBuilding ?? 'something not yet known'}
                </div>
              )}
              {job.assigned > 0 && job.efficiency < 0.999 ? (
                <div className="job-starved">
                  Working at {Math.round(job.efficiency * 100)}% — inputs run short
                </div>
              ) : null}
            </div>

            <div className="job-controls">
              <button
                type="button"
                className="btn ghost"
                onClick={(event) => step(event, -1)}
                disabled={job.assigned <= 0}
                aria-label={`Remove a worker from ${job.def.workers}`}
              >
                −
              </button>
              <span className="job-count">{job.assigned}</span>
              <button
                type="button"
                className="btn ghost"
                onClick={(event) => step(event, 1)}
                disabled={!job.unlocked || idle <= 0}
                aria-label={`Assign a worker to ${job.def.workers}`}
              >
                +
              </button>
            </div>
          </div>
        );
      })}
    </section>
  );
}
